import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addCustomEmailConfiguration,
  callBackOAuth,
  deleteEmailServerConfiguration,
  emailOauthSignIn,
  fetchAllEmailAssignees,
  fetchAllEmails,
  fetchBillingMailboxes,
  getBillingMailboxSettings,
  getCustomEmailConfiguration,
  getTestEmailConnection,
  updateCustomEmailConfiguration,
  updateEmailAccount,
  updateEmailConfiguration,
  verifyCustomEmailConfiguration,
} from "controller/api";
import type { ApiErrorType } from "shared";
import { sentryLogger } from "utilz";

export const useGetBillingMailboxSettings = () => {
  return useQuery({
    queryKey: ["channel", "email", "billing-mailbox"],
    queryFn: getBillingMailboxSettings,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getBillingMailboxSettings",
      }),
  });
};

export const useFetchAllEmails = () => {
  return useQuery({
    queryKey: ["channel", "emails"],
    queryFn: fetchAllEmails,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "fetchAllEmails",
      }),
  });
};

export const useCallBackOAuth = () => {
  const queryClient = useQueryClient();
  return useMutation(callBackOAuth, {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["channel", "emails"]);
    },
    onError(error: ApiErrorType, variables, context) {
      // A rejected callback leaves nothing saved — the backend removes the
      // temporary connection it made when sign-in started. The list was
      // fetched on mount, before that happened, so refetch it or the mailbox
      // that failed to connect keeps showing until the next reload.
      queryClient.invalidateQueries(["channel", "emails"]);
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "callBackOAuth",
      });
    },
  });
};

export const useEmailOauthSignIn = () => {
  return useMutation(
    ({
      provider,
      redirect,
      sharedMailboxEmail,
    }: {
      provider: "gmail" | "outlook";
      redirect?: "control_center" | "onboarding";
      sharedMailboxEmail?: string;
    }) => emailOauthSignIn(provider, redirect, sharedMailboxEmail),
    {
      onSuccess(data, variables, context) {},
      onError(error: ApiErrorType, variables, context) {
        sentryLogger({
          error: error,
          workflow: "controlCenter",
          level: "error",
          functionName: "emailOauthSignIn",
        });
      },
    },
  );
};

export const useUpdateEmailConfiguration = () => {
  const queryClient = useQueryClient();
  return useMutation(updateEmailConfiguration, {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["channel", "emails"]);
      queryClient.invalidateQueries(["channel", "email", "billing-mailbox"]);
      queryClient.invalidateQueries([
        "channel",
        "email",
        "assignees",
        variables.mappingId,
      ]);
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateEmailConfiguration",
      });
    },
  });
};

export const useFetchAllEmailAssignees = (mappingId?: string) => {
  return useQuery({
    queryKey: ["channel", "email", "assignees", mappingId],
    enabled: mappingId ? true : false,
    queryFn: () =>
      fetchAllEmailAssignees({
        mappinId: mappingId || "",
      }),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "fetchAllEmailAssignees",
      }),
  });
};

export const useUpdateEmailAccount = () => {
  return useMutation(updateEmailAccount, {
    onSuccess(data, variables, context) {},
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateEmailAccount",
      });
    },
  });
};

export const useDeleteEmailServerConfiguration = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteEmailServerConfiguration, {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["channel", "emails"]);
      queryClient.invalidateQueries(["channel", "email", "billing-mailbox"]);
      queryClient.invalidateQueries([
        "channel",
        "email",
        "assignees",
        variables.mappingId,
      ]);
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "deleteEmailServerConfiguration",
      });
    },
  });
};

export const useTestEmailConnection = (mappingId: string) => {
  return useQuery({
    queryKey: ["channel", "email", "test-connection", mappingId],
    queryFn: () => getTestEmailConnection(mappingId),
    enabled: false,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getTestEmailConnection",
      }),
  });
};

export const useAddCustomEmailConfiguration = () => {
  const queryClient = useQueryClient();
  return useMutation(addCustomEmailConfiguration, {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["channel", "emails"]);
      queryClient.invalidateQueries(["channel", "email", "billing-mailbox"]);
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "addCustomEmailConfiguration",
      });
    },
  });
};

/**
 * The billing mailboxes kept as backups. Enabled only when the caller needs
 * them — the switch-back picker — since every other screen wants the active
 * one from `useGetBillingMailboxSettings`.
 */
export const useFetchBillingMailboxes = (enabled = true) => {
  return useQuery({
    queryKey: ["channel", "email", "billing-mailbox", "all"],
    queryFn: () => fetchBillingMailboxes(),
    enabled,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "fetchBillingMailboxes",
      }),
  });
};

export const useGetCustomEmailConfiguration = (mappingId: string) => {
  return useQuery({
    // The id belongs in the key: without it every mailbox shares one cache
    // entry, so opening a second mailbox shows the first one's credentials.
    queryKey: ["channel", "email", "custom", "configuration", mappingId],
    queryFn: () => getCustomEmailConfiguration({ mappingId }),
    enabled: !!mappingId,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getCustomEmailConfiguration",
      }),
  });
};

export const useUpdateCustomEmailConfiguration = () => {
  const queryClient = useQueryClient();
  return useMutation(updateCustomEmailConfiguration, {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["channel", "emails"]);
      queryClient.invalidateQueries(["channel", "email", "billing-mailbox"]);
      // The edited credentials are cached per mailbox; without this the drawer
      // reopens on the values from before the edit.
      queryClient.invalidateQueries([
        "channel",
        "email",
        "custom",
        "configuration",
      ]);
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateCustomEmailConfiguration",
      });
    },
  });
};

export const useVerifyCustomEmailConfiguration = () => {
  return useMutation(verifyCustomEmailConfiguration, {
    onSuccess(data, variables, context) {},
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "verifyCustomEmailConfiguration",
      });
    },
  });
};
